import { Menu, MenuItem, Stack, Typography } from '@mui/material'
import debounce from 'lodash/debounce'
import { forwardRef, MouseEvent, useRef, useState } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import {
  Button,
  Icons,
  ScrollTag,
  SearchInputField,
  SearchInputFieldProps,
} from 'utoniq-ui'

import { PostType } from '@/clients/utoniq-core/schema'
import { Form, useForm, UseFormProps, useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'

export type SearchFormProps = {
  onSubmit: (values: SearchFormValues) => Promise<void>
  hiddenFilter: boolean
}

export type SearchFormValues = {
  keyword: string
  type?: PostType
  filter: string
}

const schema: UseFormProps['schema'] = o => ({
  keyword: o.string().allow('').optional(),
  type: o.string().allow('').optional(),
  filter: o.string().allow('').optional(),
})

const defaultValues: SearchFormValues = {
  keyword: '',
  type: undefined,
  filter: 'newestOrder',
}

export const SearchForm: Organism<SearchFormProps> = ({
  onSubmit,
  hiddenFilter,
}) => {
  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    formState: { isSubmitted },
  } = useForm<SearchFormValues>({
    schema,
    defaultValues,
  })
  const handleChangeKeywordDebounce = useRef(
    debounce(
      handleSubmit(({ keyword }) => onSubmit({ ...defaultValues, keyword })),
      500
    )
  )

  return (
    <OrganismFrame sx={{ px: 5, pt: 4 }}>
      <Form onSubmit={handleSubmit(onSubmit)} sx={{ bgcolor: 'transparent' }}>
        {/**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-5197
         */}
        {/*<SearchInputCell*/}
        {/*  {...register('keyword')}*/}
        {/*  onChange={async event => {*/}
        {/*    setValue('keyword', event.target.value)*/}
        {/*    await handleChangeKeywordDebounce.current()*/}
        {/*  }}*/}
        {/*/>*/}
        {!(getValues().keyword.length && isSubmitted) && (
          <>
            <ScrollTagCell
              control={control}
              setValue={setValue}
              onChange={handleSubmit(onSubmit)}
            />
            {/**
             * @description
             * Open when function is implemented in the future
             * https://app.clickup.com/t/908127/UT-5197
             */}
            {/*{!hiddenFilter && (*/}
            {/*  <FilterCell*/}
            {/*    setValue={setValue}*/}
            {/*    onChange={handleSubmit(onSubmit)}*/}
            {/*  />*/}
            {/*)}*/}
          </>
        )}
      </Form>
    </OrganismFrame>
  )
}

const SearchInputCell = forwardRef(
  (props: Omit<SearchInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consolePosts')
    return (
      <CellFrame sx={{ width: '100%' }}>
        <SearchInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type ScrollTagCellProps = {
  control: Control<SearchFormValues>
  setValue: UseFormSetValue<SearchFormValues>
  onChange: () => void
}
const ScrollTagCell: Cell<ScrollTagCellProps> = ({
  control,
  setValue,
  onChange,
}) => {
  const { t } = useTranslation('consolePosts')
  const type = useWatch({ control, name: 'type' })

  const typeOptions = [
    { value: undefined, label: t('all') },
    // { value: 'memberOnly', label: t('memberOnly') },
    { value: PostType.Video, label: t('video') },
    { value: PostType.Sound, label: t('sound') },
    { value: PostType.Image, label: t('image') },
  ]

  return (
    <CellFrame className="いむ">
      <ScrollTag
        value={type}
        tags={typeOptions}
        onClick={value => {
          setValue('type', type === value ? undefined : value)
          onChange()
        }}
      />
    </CellFrame>
  )
}

type FilterOptions = Record<'value' | 'label', string>[]

type FilterCellProps = {
  setValue: UseFormSetValue<SearchFormValues>
  onChange: () => void
}
const FilterCell: Cell<FilterCellProps> = ({ setValue, onChange }) => {
  const { t } = useTranslation('consolePosts')
  const filterOptions: FilterOptions = [
    { value: 'newestOrder', label: t('newestOrder') },
    { value: 'oldestOrder', label: t('oldestOrder') },
  ]
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOption, setSelectedOption] = useState<FilterOptions[number]>(
    filterOptions[0]
  )
  const open = Boolean(anchorEl)

  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = (
    event: MouseEvent<HTMLElement>,
    option: FilterOptions[number]
  ) => {
    setSelectedOption(option)
    setValue('filter', option.value)
    onChange()
    setAnchorEl(null)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <CellFrame sx={{ py: 1 }}>
      <Stack direction="row-reverse" spacing={2}>
        <Button variant="text" sx={{ mr: -2 }} onClick={handleClickListItem}>
          <Typography variant="caption" sx={{ mr: 2 }}>
            {selectedOption.label}
          </Typography>
          <Icons.DownArrow fontSize="xSmall" />
        </Button>
      </Stack>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {filterOptions.map((option, index) => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedOption.value}
            onClick={event => handleMenuItemClick(event, option)}
          >
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </CellFrame>
  )
}
